import { Icon, Modal } from '@components';
import { useScreenSize } from '@hooks';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as CloseIcon } from '@svgs/close.svg';
import { arrayOf, bool, func, number, object } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ProgressDropdown from './ProgressDropdown';

const SidePanelModal = ({
  containerProps,
  currentActivity,
  currentLesson,
  finalTest,
  isOpen,
  lessons,
  onActivityPress,
  onLessonPress,
  setIsOpen,
}) => {
  const { i18n } = useTranslation();
  const { isMobile } = useScreenSize();

  const currentLessonId = currentLesson?.id;

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      isVisible={isOpen}
      justifyContent="flex-start"
      modalContainerProps={{
        backgroundColor: 'transparent',
        borderRadius: '0px',
        height: '100%',
        maxHeight: '100%',
        maxWidth: '100vw',
        width: '100%',
      }}
      modalMaxHeight="100%"
      setModalVisible={setIsOpen}
      withBackdropNoise
      withCloseIcon={false}
    >
      <Flex
        bg={theme.colors.white[10]}
        border={`1px solid ${theme.colors.whiteLine[100]}`}
        flexDirection="column"
        flexShrink={0}
        maxHeight="100%"
        maxWidth={isMobile ? '100%' : 420}
        overflowY="auto"
        p={24}
        {...containerProps}
      >
        <Flex justifyContent="flex-end" mb={24} width="100%">
          <Button
            height={isMobile ? 24 : 32}
            onClick={() => setIsOpen(false)}
            variant="textButton"
            width={isMobile ? 24 : 32}
          >
            <Icon
              height={isMobile ? 24 : 32}
              SVG={CloseIcon}
              width={isMobile ? 24 : 32}
            />
          </Button>
        </Flex>
        <Flex
          borderLeftColor={theme.colors.white[30]}
          borderLeftStyle="solid"
          borderLeftWidth={2}
          flexDirection="column"
          gap={20}
          height="100%"
          width="100%"
        >
          {lessons?.map((lesson) => (
            <ProgressDropdown
              currentActivityId={currentActivity?.id}
              isCurrentLesson={
                finalTest
                  ? !(
                      finalTest?.id === currentLessonId &&
                      currentLesson?.content_type === 'test'
                    ) && lesson?.id === currentLessonId
                  : lesson?.id === currentLessonId
              }
              isLessonPassed={lesson?.is_completed}
              items={lesson?.activities}
              largeFont
              mainId={lesson?.id}
              onActivityPress={onActivityPress}
              onPress={onLessonPress?.({
                activityId: lesson?.activities?.[0]?.id,
                lessonId: lesson?.id,
              })}
              onPressEnd={closeModal}
              title={lesson?.title}
            />
          ))}

          {finalTest && (
            <Flex flexDirection="column" gap={8} pl={42}>
              <Flex alignItems="center" gap={10} position="relative">
                {finalTest?.id === currentLessonId &&
                  currentLesson?.content_type === 'test' && (
                    <Flex
                      backgroundColor={theme.colors.white[100]}
                      borderRadius="50%"
                      display={
                        finalTest?.id === currentLessonId ? 'flex' : 'none'
                      }
                      height={10}
                      left={0}
                      position="absolute"
                      transform="translate(-480%, 0)"
                      width={10}
                    />
                  )}
                <Button
                  height={isMobile ? 25 : 50}
                  onClick={() => {
                    onLessonPress?.({
                      activityId: finalTest?.id,
                      isFinalTest: true,
                      lessonId: finalTest?.id,
                    })();
                    closeModal();
                  }}
                  p={0}
                  variant="textButton"
                >
                  <Typography
                    color={theme.colors.white[100]}
                    textAlign="left"
                    variant="MS-semibold-s18-lh19"
                  >
                    {i18n.t('TagFormat.Test.FinalTest')}
                  </Typography>
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

SidePanelModal.propTypes = {
  containerProps: object,
  currentActivity: object.isRequired,
  currentLesson: object.isRequired,
  finalTest: object,
  isOpen: bool.isRequired,
  lastLessonId: number.isRequired,
  lessons: arrayOf(object).isRequired,
  onActivityPress: func.isRequired,
  onLessonPress: func.isRequired,
  setIsOpen: func.isRequired,
};

SidePanelModal.defaultProps = {
  containerProps: {},
  finalTest: null,
};

export default SidePanelModal;
